import React from 'react';

import { useConfiguration } from '../../../context/configurationContext';
import { NamedLink } from '../../../components';

import css from './HaulLanding.module.css';

/**
 * Haul.Taxi code-owned landing page ("Industrial Haul").
 *
 * The story is the marketplace itself: a customer photographs their junk and
 * posts the job → verified, insured haulers bid on it → the customer picks the
 * best price. ("Uber for junk hauling.") AI price estimation is a supporting
 * feature (a free ballpark while the bids come in), never the headline.
 *
 * IA: hero with a post-a-job widget + a live-bids card → "what we haul" grid →
 * how-it-works (post · bid · pick) → schedule → verification + become-a-hauler +
 * business feature rows → closing CTA. Reinterpreted in the Industrial Haul
 * visual language (asphalt + electric-blue, heavy Archivo display, hard edges).
 * Copy is hardcoded (not i18n / not the Console landing CMS asset) on purpose so
 * the homepage lives in code and can't be overridden by hosted Console assets.
 * See memory: code-ownership-moat, haul-product-vision.
 *
 * @component
 * @returns {JSX.Element}
 */

/* ---- small inline icon set (single-stroke, currentColor) ---- */
const Icon = ({ children, size = 26 }) => (
  <svg
    className={css.icon}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const IconPin = () => (
  <Icon>
    <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" />
    <circle cx="12" cy="10" r="2.5" />
  </Icon>
);
const IconCamera = () => (
  <Icon>
    <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7H7l1.2-2h7.6L17 7h2.5A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-9Z" />
    <circle cx="12" cy="13" r="3.2" />
  </Icon>
);
const IconSofa = () => (
  <Icon>
    <path d="M4 11V8.5A2.5 2.5 0 0 1 6.5 6h11A2.5 2.5 0 0 1 20 8.5V11" />
    <path d="M4 11a2 2 0 0 0-2 2v3h20v-3a2 2 0 0 0-2-2 2 2 0 0 0-2 2v1H6v-1a2 2 0 0 0-2-2Z" />
    <path d="M5 18v2M19 18v2" />
  </Icon>
);
const IconFridge = () => (
  <Icon>
    <rect x="6" y="3" width="12" height="18" rx="2" />
    <path d="M6 10h12M9 6v2M9 13v3" />
  </Icon>
);
const IconBricks = () => (
  <Icon>
    <path d="M3 8h7V4H3zM14 8h7V4h-7zM3 20h7v-4H3zM14 20h7v-4h-7zM10 14h4v-4h-4z" />
  </Icon>
);
const IconLeaf = () => (
  <Icon>
    <path d="M5 19c0-8 6-13 14-13 0 9-5 14-14 13Z" />
    <path d="M5 19c3-4 6-6 9-7" />
  </Icon>
);
const IconBoxes = () => (
  <Icon>
    <path d="M3 8 7.5 6 12 8l-4.5 2L3 8Z" />
    <path d="M3 8v5l4.5 2V10" />
    <path d="M12 8v5l-4.5 2" />
    <path d="M12 8 16.5 6 21 8l-4.5 2-4.5-2Z" />
    <path d="M12 12.5 16.5 10.5 21 12.5l-4.5 2-4.5-2Z" />
    <path d="M12 12.5v5l4.5 2v-5M21 12.5v5l-4.5 2" />
  </Icon>
);
const IconHome = () => (
  <Icon>
    <path d="M4 11.5 12 4l8 7.5" />
    <path d="M6 10v9h12v-9" />
    <path d="M10 19v-5h4v5" />
  </Icon>
);
const IconTruck = () => (
  <Icon>
    <path d="M3 6h10v9H3zM13 9h4l4 3.5V15h-8z" />
    <circle cx="7" cy="17.5" r="1.7" />
    <circle cx="17" cy="17.5" r="1.7" />
  </Icon>
);
const IconPallet = () => (
  <Icon>
    <rect x="6" y="3.5" width="12" height="6" rx="1" />
    <rect x="8.5" y="9.5" width="7" height="5" rx="1" />
    <path d="M3 18h18M6 18v2.5M12 18v2.5M18 18v2.5" />
  </Icon>
);
const IconLocker = () => (
  <Icon>
    <rect x="4" y="3.5" width="16" height="17" rx="1" />
    <path d="M4 8h16M4 12h16M4 16h16" />
    <path d="M10.5 20.5v-2.5h3v2.5" />
  </Icon>
);

const CATEGORIES = [
  { Icon: IconSofa, label: 'Furniture', desc: 'Couches, mattresses, desks, the lot.' },
  {
    Icon: IconFridge,
    label: 'Appliances',
    desc: 'Fridges, washers, water heaters — hauled & recycled.',
  },
  {
    Icon: IconBricks,
    label: 'Construction debris',
    desc: 'Renovation rubble, drywall, fixtures, flooring.',
  },
  { Icon: IconLeaf, label: 'Yard waste', desc: 'Branches, fencing, soil, storm cleanup.' },
  {
    Icon: IconBoxes,
    label: 'Garage & basement',
    desc: 'Full cleanouts — boxes, junk, mystery piles.',
  },
  { Icon: IconHome, label: 'Estate cleanout', desc: 'Whole-home clears, handled with care.' },
  { Icon: IconTruck, label: 'Moving', desc: 'Homes, apartments, offices — loaded and driven.' },
  {
    Icon: IconPallet,
    label: 'Large deliveries',
    desc: 'Bigger trucks, bigger loads, longer distances.',
  },
  {
    Icon: IconLocker,
    label: 'Storage clean-out',
    desc: 'Empty a unit fast — hauled, donated, or tossed.',
  },
];

/* the live-bids card in the hero: a posted job + verified haulers bidding */
const SAMPLE_BIDS = [
  { name: 'Marcus R.', price: '$140', low: true },
  { name: 'Dana K.', price: '$165', low: false },
  { name: 'Tony’s Hauling', price: '$190', low: false },
];

/* scrolling ticker of everything we haul (replaces the static divider) */
const MARQUEE_ITEMS = [
  'Couches',
  'Mattresses',
  'Refrigerators',
  'Hot tubs',
  'Washers & dryers',
  'Construction debris',
  'Yard waste',
  'Pianos',
  'Exercise equipment',
  'Office furniture',
  'Garage cleanouts',
  'Estate cleanouts',
  'Electronics',
  'Sheds & fencing',
  'Carpet & flooring',
  'Hot water heaters',
];

/* the checks a hauler clears before they can bid */
const VERIFY_CHECKS = [
  'Vehicle & truck confirmed',
  'Driver’s license verified',
  'Proof of insurance',
  // Was "Background-checked" — no criminal background check runs yet (see Phase-2 Checkr owner
  // task). Restore that claim only once a real check is live; until then advertise a true signal.
  'Identity verified',
];

const HaulLanding = () => {
  // AI job scoping (see configDefault.aiJobScopeEnabled). When the flag is off we drop the
  // supporting copy so the page doesn't promise a feature that isn't live. Re-enabling it
  // (REACT_APP_AI_JOB_SCOPE_ENABLED=true) brings this copy back.
  //
  // Note what this copy does and does NOT claim: the AI reads the photos to work out what the job
  // needs and which haulers to route it to. It does not estimate a price — pricing is what the
  // competing bids are for, and quoting a number up front is exactly what deters customers.
  //
  // The no-sign-up CTA below is deliberately NOT behind this flag: it is the simplest way into the
  // marketplace (photos + address + email → job posted), and it works with the AI off.
  const config = useConfiguration();
  const aiJobScopeEnabled = !!config?.aiJobScopeEnabled;
  return (
    <div className={css.root}>
      {/* ===================== HERO ===================== */}
      <section className={css.hero}>
        <div className={css.heroInner}>
          <div className={css.heroCopy}>
            <p className={css.locChip}>
              <span className={css.locDot} /> Wichita, KS
              <NamedLink name="SearchPage" className={css.locChange}>
                Change city
              </NamedLink>
            </p>
            <h1 className={css.heroTitle}>
              <span className={css.line}>Need it hauled away?</span>
              <span className={css.lineAccent}>Post it. Haulers bid.</span>
            </h1>
            <p className={css.heroSub}>
              Furniture, junk, a whole cleanout, a move — snap a photo and post it. Verified,
              insured haulers compete for the job, and you pick the best price and the pro.
              {aiJobScopeEnabled
                ? ' We read your photos and route the job to haulers with the right gear.'
                : ''}
            </p>

            {/* Acquisition hook: the no-account front door — photos + address + email and the job
                is live. Always shown; it depends on Turnstile, not on the AI. */}
            <NamedLink name="GuestQuotePage" className={css.heroQuoteCta}>
              Post Your Job in 60 Seconds
              <span className={css.heroQuoteCtaNote}>No sign-up required</span>
            </NamedLink>

            {/* Functional "post a job" widget (the hero's action surface) */}
            <div className={css.widget}>
              <span className={css.widgetLabel}>Post your job</span>
              <NamedLink name="NewListingPage" className={css.widgetRow}>
                <IconPin />
                <span>Wichita, KS</span>
                <span className={css.widgetRowHint}>Change</span>
              </NamedLink>
              <NamedLink name="NewListingPage" className={css.widgetRow}>
                <IconCamera />
                <span>Add a photo of your junk</span>
                <span className={css.widgetArrow}>→</span>
              </NamedLink>
              <div className={css.widgetActions}>
                <NamedLink name="NewListingPage" className={css.widgetCta}>
                  Get free bids
                </NamedLink>
                <NamedLink name="LoginPage" className={css.widgetAlt}>
                  Log in to see your jobs
                </NamedLink>
              </div>
            </div>

            <div className={css.trustRow}>
              <span>Verified haulers</span>
              <span className={css.dot} />
              <span>You pick the price</span>
              <span className={css.dot} />
              <span>Free to post</span>
            </div>
          </div>

          {/* Live bids visualization — the marketplace in one card */}
          <aside className={css.heroVisual} aria-hidden="true">
            <div className={css.bidCard}>
              <div className={css.bidPhoto}>
                <span className={css.bidPostedTag}>Posted 4 min ago</span>
                <div className={css.bidChips}>
                  <span>couch</span>
                  <span>mattress</span>
                  <span>3 boxes</span>
                </div>
              </div>
              <div className={css.bidBody}>
                <div className={css.bidHead}>
                  <span className={css.bidJobTitle}>Garage cleanout</span>
                  <span className={css.bidLive}>
                    <span className={css.bidLiveDot} />3 bids
                  </span>
                </div>
                <ul className={css.bidList}>
                  {SAMPLE_BIDS.map(({ name, price, low }) => (
                    <li className={low ? `${css.bidRow} ${css.bidRowLow}` : css.bidRow} key={name}>
                      <span className={css.bidHauler}>
                        <span className={css.bidCheck} />
                        {name}
                      </span>
                      {low ? <span className={css.bidBadge}>Best price</span> : null}
                      <span className={css.bidPrice}>{price}</span>
                    </li>
                  ))}
                </ul>
                <div className={css.bidFoot}>
                  <span className={css.bidFootLabel}>You choose — pick any bid</span>
                  {aiJobScopeEnabled ? (
                    <span className={css.bidFootAi}>Matched on equipment</span>
                  ) : null}
                </div>
              </div>
            </div>
          </aside>
        </div>
        {/* scrolling ticker of everything we haul */}
        <div className={css.marquee} aria-hidden="true">
          <div className={css.marqueeTrack}>
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
              <span className={css.marqueeItem} key={i}>
                {item}
                <span className={css.marqueeSep} />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CATEGORIES ===================== */}
      <section className={css.cats}>
        <div className={css.sectionHead}>
          <p className={css.eyebrowDark}>What can we haul?</p>
          <h2 className={css.sectionTitle}>If you can pile it, we can take it.</h2>
        </div>
        <ul className={css.catGrid}>
          {CATEGORIES.map(({ Icon: CatIcon, label, desc }) => (
            <li key={label}>
              <NamedLink name="NewListingPage" className={css.catCard}>
                <span className={css.catIcon}>
                  <CatIcon />
                </span>
                <span className={css.catLabel}>{label}</span>
                <span className={css.catDesc}>{desc}</span>
                <span className={css.catLink}>Post this job →</span>
              </NamedLink>
            </li>
          ))}
        </ul>
      </section>

      {/* ===================== HOW IT WORKS ===================== */}
      <section className={css.how} id="how">
        <div className={css.sectionHead}>
          <p className={css.eyebrowDark}>How it works</p>
          <h2 className={css.sectionTitle}>Post it. They bid. It’s gone.</h2>
        </div>
        <ol className={css.steps}>
          <li className={css.step}>
            <span className={css.stepNum}>01</span>
            <h3 className={css.stepTitle}>Post</h3>
            <p className={css.stepBody}>
              Snap a photo of the pile and post the job — takes about a minute.
              {aiJobScopeEnabled
                ? " We'll work out what it needs and find haulers equipped for it."
                : ''}
            </p>
          </li>
          <li className={css.step}>
            <span className={css.stepNum}>02</span>
            <h3 className={css.stepTitle}>Compare bids</h3>
            <p className={css.stepBody}>
              Verified, insured haulers bid on your job. Compare their prices, ratings, and pickup
              times side by side.
            </p>
          </li>
          <li className={css.step}>
            <span className={css.stepNum}>03</span>
            <h3 className={css.stepTitle}>Pick &amp; done</h3>
            <p className={css.stepBody}>
              Choose the bid you like, schedule the pickup, and it’s gone. Pay securely in-app once
              the job is done.
            </p>
          </li>
        </ol>
      </section>

      {/* ===================== SCHEDULE (after you pick a bid) ===================== */}
      <section className={css.schedule}>
        <div className={css.scheduleInner}>
          <div className={css.scheduleCard}>
            <span className={css.scheduleKicker}>After you pick a bid</span>
            <h2 className={css.scheduleTitle}>Book the pickup on your schedule.</h2>
            <div className={css.scheduleFields} aria-hidden="true">
              <div className={css.scheduleField}>
                <span className={css.scheduleFieldLabel}>Date</span>
                <span className={css.scheduleFieldValue}>Jun 30</span>
              </div>
              <div className={css.scheduleField}>
                <span className={css.scheduleFieldLabel}>Window</span>
                <span className={css.scheduleFieldValue}>8–10 AM</span>
              </div>
            </div>
            <NamedLink name="NewListingPage" className={css.scheduleCta}>
              Post a job
            </NamedLink>
          </div>
          <ul className={css.scheduleBenefits}>
            <li>
              <span className={css.benefitMark} />
              Pick an exact pickup window — up to 30 days out.
            </li>
            <li>
              <span className={css.benefitMark} />
              Your chosen hauler is locked in before pickup day.
            </li>
            <li>
              <span className={css.benefitMark} />
              Free cancellation up to 2 hours before your window.
            </li>
          </ul>
        </div>
      </section>

      {/* ===================== FEATURE ROWS (zigzag) ===================== */}
      <section className={css.features}>
        {/* Verification — the trust pillar (footer deep-links here via /#verify) */}
        <div className={css.feature} id="verify">
          <div className={css.featureCopy}>
            <p className={css.eyebrowDark}>Verified, not random</p>
            <h2 className={css.featureTitle}>Only verified haulers can bid.</h2>
            <p className={css.featureBody}>
              Before anyone can bid on your job, we confirm who they are and what they drive. No
              strangers, no surprises — just insured pros you can rate after every haul.
            </p>
            <ul className={css.verifyList}>
              {VERIFY_CHECKS.map(check => (
                <li className={css.verifyItem} key={check}>
                  <span className={css.verifyTick} />
                  {check}
                </li>
              ))}
            </ul>
          </div>
          <div className={css.featurePanel} aria-hidden="true">
            <svg viewBox="0 0 120 120" className={css.panelArt}>
              <path
                className={css.panelStroke}
                d="M60 16 100 30v26c0 26-18 40-40 50-22-10-40-24-40-50V30L60 16Z"
              />
              <path className={css.panelAccent} d="M44 60l11 11 22-24" />
            </svg>
            <span className={css.panelTag}>Verified</span>
          </div>
        </div>

        {/* Become a hauler */}
        <div className={`${css.feature} ${css.featureReverse}`}>
          <div className={css.featureCopy}>
            <p className={css.eyebrowDark}>Drive a truck?</p>
            <h2 className={css.featureTitle}>Get verified. Bid on jobs. Earn.</h2>
            <p className={css.featureBody}>
              Turn an empty truck bed into income. Verify your vehicle, license, and insurance once,
              then bid on the jobs you want, get paid fast, and build a route around your day.
            </p>
            <NamedLink name="SignupPage" className={css.featurePrimary}>
              Become a hauler
            </NamedLink>
          </div>
          <div className={`${css.featurePanel} ${css.featurePanelWarm}`} aria-hidden="true">
            <svg viewBox="0 0 120 120" className={css.panelArt}>
              <path className={css.panelStroke} d="M14 78h54V44h22l16 18v16h-8" />
              <path className={css.panelStroke} d="M14 78V40h54v38" />
              <circle className={css.panelAccent} cx="38" cy="84" r="8" />
              <circle className={css.panelAccent} cx="86" cy="84" r="8" />
            </svg>
            <span className={css.panelTag}>Keep 80%</span>
          </div>
        </div>

        {/* For business (footer deep-links here via /#business) */}
        <div className={css.feature} id="business">
          <div className={css.featureCopy}>
            <p className={css.eyebrowDark}>Haul.Taxi for business</p>
            <h2 className={css.featureTitle}>Cleanouts at scale, on one account.</h2>
            <p className={css.featureBody}>
              Property managers, contractors, and offices — post recurring jobs, collect bids, track
              every haul, and bill it all to one place. Volume pricing, zero hassle.
            </p>
            <NamedLink name="SignupPage" className={css.featurePrimary}>
              Set up a business account
            </NamedLink>
          </div>
          <div className={`${css.featurePanel} ${css.featurePanelGrid}`} aria-hidden="true">
            <svg viewBox="0 0 120 120" className={css.panelArt}>
              <path className={css.panelStroke} d="M22 100V34h34v66M56 100V52h42v48" />
              <path
                className={css.panelAccent}
                d="M32 46h6M40 46h6M32 58h6M40 58h6M32 70h6M40 70h6M68 64h8M82 64h8M68 78h8M82 78h8"
              />
            </svg>
            <span className={css.panelTag}>One invoice</span>
          </div>
        </div>
      </section>

      {/* ===================== CTA BAND ===================== */}
      <section className={css.cta}>
        <div className={css.ctaInner}>
          <h2 className={css.ctaTitle}>Got junk?</h2>
          <p className={css.ctaSub}>
            Post your job and let verified haulers bid for it. Free to post — you only pay when you
            pick a bid.
          </p>
          <NamedLink name="NewListingPage" className={css.ctaPrimaryBig}>
            Post your job →
          </NamedLink>
        </div>
      </section>
    </div>
  );
};

export default HaulLanding;
